import XButton from 'src/components/Buttons/XButton';
import PnlModalCardPreview from 'src/components/Modals/PnlModal/PnlModalCardPreview';
import PnlModalPaginationDisplay from 'src/components/Modals/PnlModal/PnlModalPaginationDisplay';
import PnlModalOptions from 'src/components/Modals/PnlModal/PnlModalOptions';
import PnlModalButtons from 'src/components/Modals/PnlModal/PnlModalButtons/PnlModalButtons';
import PnlModalPaginationControls from 'src/components/Modals/PnlModal/PnlModalPaginationControls';
import BaseModal from 'src/components/primitives/BaseModal/BaseModal';
import { PnlModalCardPreviewVariantProps } from 'src/components/Modals/PnlModal/types';
import Divider from 'src/components/primitives/Divider/Divider';
import { cj } from 'src/utils/classNames';

interface Props {
	handleClose: () => void;
	backgroundImagePathIndex: number;
	setBackgroundImagePathIndex: (index: number) => void;
	imageGenerationNodeRef: React.RefObject<HTMLDivElement | null>;
	showCollateral: boolean;
	setShowCollateral: (showCollateral: boolean) => void;
	handleDownload: () => void;
	handleCopy: () => void;
	isCopying: boolean;
	isCopied: boolean;
	isDownloading: boolean;
	cardPreviewVariantProps: PnlModalCardPreviewVariantProps;
}

export default function PnlModalDesktop({
	handleClose,
	backgroundImagePathIndex,
	setBackgroundImagePathIndex,
	imageGenerationNodeRef,
	showCollateral,
	setShowCollateral,
	handleDownload,
	handleCopy,
	isCopying,
	isCopied,
	isDownloading,
	cardPreviewVariantProps,
}: Props) {
	const { variant, positionData } = cardPreviewVariantProps;
	const isFromPositionHistory =
		positionData?.triggeredFrom === 'position_history_table';
	const shouldShowCollateral = showCollateral && !isFromPositionHistory;

	return (
		<BaseModal.Container
			onClose={handleClose}
			className={cj(
				// `px-14` provides padding where the `X` and pagination controls sit.
				// Since there's extra padding, we remove the shadow here and add it manually to the content.
				'relative overflow-visible bg-transparent px-14 shadow-none',
				variant === 'portfolio-performance'
					? // `w-[538px]` is a magic number: 416 (PnL card width) + 2 (PnL modal border) + 120 (padding).
						'w-[538px] max-w-[538px]'
					: // `w-[702px]` is a magic number: 580 (PnL card width) + 2 (PnL modal border) + 120 (padding).
						'w-[702px] max-w-[702px]'
			)}
			dataTestId="pnl-modal"
		>
			<XButton
				size="lg"
				variant="primary"
				className="absolute top-0 right-0"
				onClick={handleClose}
				dataTestId="pnl-modal-close"
			/>
			<div className="overflow-hidden rounded-sm shadow-md">
				<PnlModalPaginationControls
					className="top-1/2 -translate-y-1/2"
					xOffset={0}
					backgroundImagePathIndex={backgroundImagePathIndex}
					setBackgroundImagePathIndex={setBackgroundImagePathIndex}
					marketIndex={positionData?.position.marketIndex}
					variant={variant}
				/>
				<PnlModalCardPreview
					imageGenerationNodeRef={imageGenerationNodeRef}
					backgroundImagePathIndex={backgroundImagePathIndex}
					showCollateral={shouldShowCollateral}
					cardPreviewVariantProps={cardPreviewVariantProps}
				/>
				<BaseModal.Title
					// Use `sr-only` to keep the title for a11y.
					className={cj(variant === 'portfolio-performance' && 'sr-only')}
					innerWrapperClassName="border-none"
					endElement={
						<PnlModalPaginationDisplay
							backgroundImagePathIndex={backgroundImagePathIndex}
							marketIndex={positionData?.position.marketIndex}
							variant={variant}
						/>
					}
				>
					Share P&L
				</BaseModal.Title>
				<BaseModal.Body
					className={cj(
						variant === 'portfolio-performance'
							? 'border-t border-stroke-divider'
							: 'pt-0'
					)}
				>
					{variant !== 'portfolio-performance' && <Divider />}

					{variant !== 'portfolio-performance' && !isFromPositionHistory && (
						<PnlModalOptions
							className="items-center justify-between"
							showCollateral={showCollateral}
							setShowCollateral={setShowCollateral}
							triggeredFromPositionClose={
								positionData?.triggeredFrom === 'position_closed'
							}
						/>
					)}

					<PnlModalButtons
						handleClose={handleClose}
						handleDownload={handleDownload}
						handleCopy={handleCopy}
						isDownloading={isDownloading}
						isCopying={isCopying}
						isCopied={isCopied}
					/>
				</BaseModal.Body>
			</div>
		</BaseModal.Container>
	);
}
